import { forwardRef, useState } from 'react';
import { NudosButton, NudosSelectButton } from '../../../../../../../../../components/NudosComponents';
import {
  TEMPORARY_STORAGE_REASON_TYPE_ID,
  TEMPORARY_STORAGE_REASON_TYPE_NAME
} from '../../../../../../../../../types/orders';
import { cancelTemporaryStorageForShipment } from '../../../../../../../../../services/orders';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../../../utils/displayNudosStandardNotifications';
import './TemporaryStorageDropdown.scss';

export const TemporaryStorageDropdown = forwardRef<HTMLDivElement, ITemporaryStorageDropdownProps>((props, ref) => {
  const { continueCallback, initialReasons, shipmentId, reloadDataCallback, closeDropdownCallback } = props;
  const [reasons, setReasons] = useState<TEMPORARY_STORAGE_REASON_TYPE_ID[]>(initialReasons || []);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isButtonDisabled = !shipmentId || (!initialReasons && reasons.length === 0) || isLoading;

  const handleClickReason = (event: React.MouseEvent<HTMLDivElement>, reasonId: TEMPORARY_STORAGE_REASON_TYPE_ID) => {
    event?.stopPropagation();
    setReasons(reasons.includes(reasonId) ? reasons.filter(id => id !== reasonId) : [...reasons, reasonId]);
  };

  const handleContinue = async () => {
    if (isButtonDisabled) return;
    try {
      setIsLoading(true);
      const cancelTemporaryStorageIntent = initialReasons?.length && reasons.length === 0;

      if (!cancelTemporaryStorageIntent) return continueCallback(reasons);

      await cancelTemporaryStorageForShipment(shipmentId);
      await reloadDataCallback?.();
      closeDropdownCallback();
      return displaySuccessNotification({
        customJSXMessage: <div>Parada en bodega cancelada correctamente</div>,
        duration: 10000
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const temporaryStorageReasons = [
    {
      id: TEMPORARY_STORAGE_REASON_TYPE_ID.CONFIGURATION,
      name: TEMPORARY_STORAGE_REASON_TYPE_NAME.CONFIGURATION,
      isSelected: reasons.includes(TEMPORARY_STORAGE_REASON_TYPE_ID.CONFIGURATION),
      handleClick: (event: React.MouseEvent<HTMLDivElement>) =>
        handleClickReason(event, TEMPORARY_STORAGE_REASON_TYPE_ID.CONFIGURATION)
    },
    {
      id: TEMPORARY_STORAGE_REASON_TYPE_ID.PREPARATION,
      name: TEMPORARY_STORAGE_REASON_TYPE_NAME.PREPARATION,
      isSelected: reasons.includes(TEMPORARY_STORAGE_REASON_TYPE_ID.PREPARATION),
      handleClick: (event: React.MouseEvent<HTMLDivElement>) =>
        handleClickReason(event, TEMPORARY_STORAGE_REASON_TYPE_ID.PREPARATION)
    },
    {
      id: TEMPORARY_STORAGE_REASON_TYPE_ID.INVOICE,
      name: TEMPORARY_STORAGE_REASON_TYPE_NAME.INVOICE,
      isSelected: reasons.includes(TEMPORARY_STORAGE_REASON_TYPE_ID.INVOICE),
      handleClick: (event: React.MouseEvent<HTMLDivElement>) =>
        handleClickReason(event, TEMPORARY_STORAGE_REASON_TYPE_ID.INVOICE)
    }
  ];

  return (
    <div className="temporaryStorageDropdown" ref={ref}>
      <h3>¿Razón de la parada en bodega?</h3>
      <div className="reasons">
        {temporaryStorageReasons.map(reason => (
          <div className="reason" key={reason.id}>
            <NudosSelectButton
              componentSize="xs"
              colorPalette="black"
              handleClickButton={reason.handleClick}
              isButtonActive={reason.isSelected}
            />
            <p>{reason.name}</p>
          </div>
        ))}
      </div>
      <NudosButton
        customHeight="18px"
        customWidth="80px"
        buttonText="Continuar"
        componentSize="extraSmall"
        isButtonDisabled={isButtonDisabled}
        handleClick={handleContinue}
      />
    </div>
  );
});

interface ITemporaryStorageDropdownProps {
  shipmentId: number;
  continueCallback: (reasons: TEMPORARY_STORAGE_REASON_TYPE_ID[]) => void;
  initialReasons?: TEMPORARY_STORAGE_REASON_TYPE_ID[];
  reloadDataCallback?: () => Promise<void>;
  closeDropdownCallback: () => void;
}
